import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import OnDeleteIcon from '../../../icons/OnDeleteIcon';
import {
    clearAppointmentAuditData,
    clearNewAppointment,
    deleteSchedulingAssistantAppointmentSlot,
    deleteUserCalendarAppointmentSlot,
    loadAppointmentAuditData,
    loadAppointmentSlot,
    setOpenAppointmentSchedulerPanel,
    setUsersCurrentAppointment,
    updateSchedulingAssistantAppointmentSlot,
    updateUserCalendarAppointmentSlot
} from '../../../store/calendarService';
import {
    loadLocationsByServiceId,
    loadServiceDetailsByService
} from '../../../store/directusService';
import { clearParticipantsSearch, searchParticipants } from '../../../store/participantService';
import { selectUsersForNamesById } from '../../../store/userSelectors';
import { loadTeam, loadUsersMinimalDetails } from '../../../store/userService';
import { isTimePast, reverseFormatDate } from '../../../utils/dateFunctions';
import { getConfiguredItems, getNameFromId } from '../../../utils/directusFunctions';
import { createHash } from '../../../utils/stringUtils';
import { hasRole } from '../../../utils/userRoles';
import AuditAppointmentModal from '../../auditing/auditAppointments/AuditAppointmentModal';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import StaticField from '../../formElements/StaticField';
import Switch from '../../formElements/Switch';
import TextInputField from '../../formElements/TextInputField';
import Panel from '../../ui/panel/Panel';
import AdviserSelect from '../components/AdviserSelect';
import AppointmentTypeSelect from '../components/AppointmentTypeSelect';
import LocationSelect from '../components/LocationSelect';
import ParticipantsSelect from '../components/ParticipantsSelect';
import StartAndEndTimesSelect from '../components/StartAndEndTimesSelect';
import StartAppointmentSlotsRows from '../components/StartAppointmentSlotsRows';
import {
    acceptedRoles,
    cancelledStatuses,
    canEditSlot,
    deleteRoles,
    getServiceIds,
    group1,
    unresolvedStatuses
} from '../utils/calendarUtils';
import { validate } from '../validation/validateAppointmentSlotsEdit';
import { ADDITIONAL_DETAILS_LIMIT } from '../validation/validationFunctions';

import sidePanel from '../styles/schedulePanel.module.css';

const UserAppointmentSlotsEdit = ({ usersCurrentAppointment, calendar = '' }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        typeId: '',
        userId: '',
        locationId: '',
        startTime: '08:00',
        endTime: '09:00',
        date: String(new Date().toISOString().split('T')[0]),
        serviceId: '',
        teamId: '',
        participantIds: [],
        numberOfSlots: 1,
        type: '',
        service: '',
        location: '',
        additionalDetails: ''
    };

    const [initialStateOnEntry, setInitialStateOnEntry] = useState({});
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [appointmentAdviser, setAppointmentAdviser] = useState({});
    const [filteredAppointmentTypes, setFilteredAppointmentTypes] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [reservedAppointments, setReservedAppointments] = useState([]);
    const [isDisabledStartSlotButton, setIsDisabledStartSlotButton] = useState(false);
    const [isDisabledUpdateSlotButton, setIsDisabledUpdateSlotButton] = useState(false);
    const [statuses, setStatuses] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [areAnySlotsResolved, setAreAnySlotsResolved] = useState(false);
    const [isInPast, setIsInPast] = useState(false);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [auditParticipantId, setAuditParticipantId] = useState('');
    const [auditModalHeadingLabel, setAuditModalHeadingLabel] = useState('');
    const [hash, setHash] = useState('01');
    const [hasParticipantAccess, setHasParticipantAccess] = useState({});

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector(selectUsersForNamesById);
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const allTeams = useSelector((state) => state.entities.userService.allTeams);
    const locationsByServiceId = useSelector(
        (state) => state.entities.directusService.locationsByServiceId
    );
    const participantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const loadingParticipantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch.loadingParticipantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const appointment = useSelector(
        (state) => state.entities.calendarService.usersCurrentAppointment
    );
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!('id' in usersCurrentAppointment) || !('serviceId' in usersCurrentAppointment)) return;
        dispatch(loadAppointmentSlot(usersCurrentAppointment.id));
        dispatch(searchParticipants({ serviceId: usersCurrentAppointment.serviceId }, 0));
    }, []);

    useEffect(() => {
        if (
            !participantsSearchMetaData.last &&
            !loadingParticipantsSearch &&
            'number' in participantsSearchMetaData
        ) {
            dispatch(
                searchParticipants(
                    { serviceId: usersCurrentAppointment.serviceId },
                    participantsSearchMetaData.number + 1
                )
            );
        } else if (participantsSearchMetaData?.last) setParticipants(participantsSearch);
    }, [participantsSearch, participantsSearchMetaData, loadingParticipantsSearch]);

    useEffect(() => {
        if (!('id' in appointment) || appointment.id !== usersCurrentAppointment?.id) return;

        if ('location' in appointment) {
            /* eslint-disable no-unused-vars */
            const { location, ...rest } = appointment;
            setInitialStateOnEntry(rest);
        }

        if (appointment?.serviceId) {
            const { serviceId } = appointment;

            if (!(serviceId in serviceDetailsById))
                dispatch(loadServiceDetailsByService([serviceId]));

            if (!(serviceId in locationsByServiceId)) {
                dispatch(loadLocationsByServiceId([serviceId]));
            }
        }

        if (appointment?.teamId) {
            const team = allTeams.find((el) => el.id === appointment.teamId);
            if (!team) dispatch(loadTeam(appointment.teamId));
        }
    }, [appointment]);

    useEffect(() => {
        if (!(appointment.userId in users) && 'userId' in usersCurrentAppointment)
            dispatch(loadUsersMinimalDetails([usersCurrentAppointment.userId]));
        else setAppointmentAdviser(users[usersCurrentAppointment.userId]);
    }, [users]);

    useEffect(() => {
        if (
            !('id' in appointment) ||
            appointment.id !== usersCurrentAppointment.id ||
            !(appointment?.serviceId in serviceDetailsById)
        )
            return;
        setNewEntry({
            ...appointment,
            service: serviceDetailsById[appointment.serviceId].name
        });
        setIsInPast(isTimePast(`${appointment.date}T${appointment.startTime}`));
        const contractId = serviceDetailsById[appointment.serviceId]?.contract?.id;
        if (contractId && appointmentTypeDetails?.length) {
            setFilteredAppointmentTypes(getConfiguredItems(appointmentTypeDetails, contractId));
        }

        setIsDisabledStartSlotButton(!hasRole(acceptedRoles, roles));
        setIsDisabledUpdateSlotButton(!hasRole(group1, roles));
    }, [serviceDetailsById, appointment]);

    useEffect(() => {
        if (
            !participants?.length ||
            !('id' in appointment) ||
            appointment.id !== usersCurrentAppointment.id
        )
            return;
        if (appointment.participantIds?.length) {
            const selected = appointment.participantIds
                .map((el) => participants.find((entry) => entry.id === el))
                .filter((el) => el);
            setSelectedParticipants(selected);

            const usersServiceIds = getServiceIds(loggedInUser);
            const accesses = selected.reduce(
                (acc, cur) => ({
                    ...acc,
                    [cur.id]: usersServiceIds.some((el) => el === cur.serviceId)
                }),
                {}
            );
            setHasParticipantAccess(accesses);
        }
    }, [participants, appointment]);

    useEffect(() => {
        if (
            !selectedParticipants?.length ||
            !('id' in appointment) ||
            appointment.id !== usersCurrentAppointment.id
        )
            return;
        const statuses = selectedParticipants.reduce(
            (acc, cur) => ({
                ...acc,
                [cur.id]:
                    appointment?.attendances.find((entry) => entry.participantId === cur.id)
                        ?.status || 'Not known'
            }),
            {}
        );
        setStatuses(statuses);
        setReservedAppointments(
            Object.values(statuses).filter((el) => !cancelledStatuses.includes(el))
        );
        setAreAnySlotsResolved(
            selectedParticipants.some((el) => !unresolvedStatuses.includes(statuses[el.id]))
        );
    }, [selectedParticipants, appointment]);

    useEffect(() => {
        if (!('id' in appointment) || appointment.id !== usersCurrentAppointment.id) return;
        setNewEntry((prev) => ({
            ...prev,
            ...appointment
        }));
        setIsDisabledStartSlotButton(!hasRole(acceptedRoles, roles));
        setIsDisabledUpdateSlotButton(!hasRole(group1, roles));
    }, [appointment]);

    useEffect(() => {
        setIsUpdateButtonDisabled(
            Object.values(errors).some((el) => el.error) || !hasRole(acceptedRoles, roles)
        );
    }, [errors]);

    useEffect(() => {
        if (successMessage === `Appointment slot ${newEntry.type} has been updated (${hash})`) {
            setIsUpdateButtonDisabled(!hasRole(acceptedRoles, roles));
            if (selectedParticipants.length < 1) onPanelClose();
        }
        if (successMessage.includes(`Appointment slot ${newEntry.type} has been deleted`)) {
            setIsUpdateButtonDisabled(!hasRole(acceptedRoles, roles));
            onPanelClose();
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            setIsUpdateButtonDisabled(!hasRole(acceptedRoles, roles));
        }
    }, [errorMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const configTitleRow = () => {
        return (
            <>
                {newEntry.service}
                <span>
                    {reservedAppointments.length} / {newEntry.numberOfSlots}
                </span>
            </>
        );
    };

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        dispatch(setUsersCurrentAppointment({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
        dispatch(clearNewAppointment());
        setTimeout(() => dispatch(clearParticipantsSearch()), 1000);
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        setAuditModalHeadingLabel('');
        dispatch(clearAppointmentAuditData());
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setIsDisabledStartSlotButton(true);
        setIsDisabledUpdateSlotButton(true);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateAppointmentType = (key, value, type) => {
        onUpdate(key, value);
        setNewEntry((prev) => ({ ...prev, type }));
    };

    const onUpdateLocation = (key, value, location) => {
        clearError(key);
        const additionalDetails =
            location?.name?.toLowerCase() !== 'community' ? '' : newEntry.additionalDetails;
        setNewEntry((prev) => ({
            ...prev,
            location: location?.name || '',
            additionalDetails,
            [key]: value
        }));
    };

    const onUpdateTime = (key, value) => {
        clearError(key === 'endTime' ? 'startTime' : 'endTime');
        onUpdate(key, value);
    };

    const onDelete = () => {
        calendar === 'schedulingAssistant'
            ? dispatch(
                  deleteSchedulingAssistantAppointmentSlot(
                      newEntry.userId,
                      newEntry.type,
                      newEntry.id
                  )
              )
            : dispatch(
                  deleteUserCalendarAppointmentSlot(newEntry.userId, newEntry.type, newEntry.id)
              );
    };

    const updateSelectedParticipants = (updatedSelectedParticipants) => {
        setIsDisabledStartSlotButton(true);
        setIsDisabledUpdateSlotButton(true);
        setSelectedParticipants(updatedSelectedParticipants);
    };

    const onSearchAuditItems = (label, modalHeadingLabel = '', participantId = '') => {
        if (newEntry.id) dispatch(loadAppointmentAuditData('appointment-slot', newEntry.id, label));
        setShowAuditModal(true);
        setAuditLabel(label);
        setAuditModalHeadingLabel(modalHeadingLabel ?? label);
        auditParticipantId !== participantId && setAuditParticipantId(participantId);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const {
            /* eslint-disable no-unused-vars */
            type,
            service,
            location,
            ...rest
        } = newEntry;
        const participantIds = selectedParticipants.map((el) => el.id);
        const payload = { ...rest, participantIds };
        const referralDates = selectedParticipants.map((el) => el.referralDate);
        const newErrors = validate(payload, initialStateOnEntry, referralDates, roles);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        const hash = createHash();
        setHash(hash);
        calendar === 'schedulingAssistant'
            ? dispatch(
                  updateSchedulingAssistantAppointmentSlot(
                      newEntry.userId,
                      newEntry.type,
                      payload,
                      hash
                  )
              )
            : dispatch(
                  updateUserCalendarAppointmentSlot(newEntry.userId, newEntry.type, payload, hash)
              );
        setInitialStateOnEntry(payload);
        setNewEntry((prev) => ({
            ...prev,
            participantIds
        }));
    };

    // RENDER
    return (
        <>
            <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
                <div className={sidePanel.panelActions}>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={(e) => setIsAuditable(e.target.checked)}
                    />
                    <OnDeleteIcon
                        onDelete={onDelete}
                        roles={roles}
                        acceptedRoles={deleteRoles}
                        active={!!newEntry.id}
                    />
                </div>
                <div className={sidePanel.formWrapper}>
                    <form onSubmit={onSubmit} data-testid="user_slots_edit_form">
                        <StaticField content={configTitleRow()} customClass="underlinedContent" />

                        <StaticField
                            id="teamId"
                            label="Team"
                            content={getNameFromId(allTeams, newEntry.teamId)}
                        />

                        {canEditSlot(areAnySlotsResolved, 'adviser', roles, isInPast) ? (
                            <AdviserSelect
                                userId={newEntry.userId || ''}
                                serviceId={newEntry.serviceId}
                                teamId={newEntry.teamId}
                                initialAdviser={appointmentAdviser}
                                onAdviserChange={(id) => onUpdate('userId', id)}
                                disabled={!newEntry.service || !hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                errors={errors}
                            />
                        ) : (
                            <StaticField
                                id="adviserId"
                                label="Adviser"
                                content={appointmentAdviser?.loginEmailAddress || ''}
                            />
                        )}

                        {canEditSlot(areAnySlotsResolved, 'appointmentType', roles, isInPast) ? (
                            <AppointmentTypeSelect
                                appointmentTypes={filteredAppointmentTypes}
                                appointmentTypeId={newEntry.typeId}
                                onAppointmentTypeChange={onUpdateAppointmentType}
                                errors={errors}
                            />
                        ) : (
                            <StaticField
                                id="type"
                                label="Appointment Type"
                                content={newEntry.type}
                            />
                        )}

                        {canEditSlot(areAnySlotsResolved, 'location', roles, isInPast) ? (
                            <>
                                <LocationSelect
                                    locationId={newEntry.locationId}
                                    serviceId={newEntry.serviceId}
                                    onLocationChange={onUpdateLocation}
                                    errors={errors}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={onSearchAuditItems}
                                />

                                {newEntry?.location?.toLowerCase() === 'community' && (
                                    <TextInputField
                                        id="additionalDetails"
                                        label="Additional Details"
                                        placeholder="Enter additional details..."
                                        maxLength={ADDITIONAL_DETAILS_LIMIT}
                                        value={newEntry.additionalDetails || ''}
                                        onChange={(e) =>
                                            onUpdate('additionalDetails', e.target.value)
                                        }
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <StaticField
                                    id="location"
                                    label="Location"
                                    content={newEntry.location}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={onSearchAuditItems}
                                />
                                {newEntry.additionalDetails && (
                                    <StaticField
                                        id="additionalDetails"
                                        label="Additional Details"
                                        content={newEntry.additionalDetails}
                                    />
                                )}
                            </>
                        )}
                        {canEditSlot(areAnySlotsResolved, 'startAndEndTime', roles, isInPast) ? (
                            <StartAndEndTimesSelect
                                startTime={newEntry.startTime}
                                endTime={newEntry.endTime}
                                onTimeChange={onUpdateTime}
                                errors={errors}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                            />
                        ) : (
                            <>
                                <StaticField
                                    id="startTime"
                                    label="Start Time"
                                    content={newEntry.startTime}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={() =>
                                        onSearchAuditItems('Start Date', 'Start Time')
                                    }
                                />
                                <StaticField
                                    id="endTime"
                                    label="End Time"
                                    content={newEntry.endTime}
                                    isAuditable={isAuditable}
                                    onSearchAuditItems={() =>
                                        onSearchAuditItems('End Date', 'End Time')
                                    }
                                />
                            </>
                        )}

                        {canEditSlot(areAnySlotsResolved, 'date', roles, isInPast) ? (
                            <DateSelect
                                mandatory={true}
                                value={newEntry.date}
                                error={errors.date}
                                onDateChange={(date) => onUpdate('date', date)}
                                isAuditable={isAuditable}
                                onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                            />
                        ) : (
                            <StaticField
                                id="date"
                                label="Date"
                                content={reverseFormatDate(newEntry.date)}
                                isAuditable={isAuditable}
                                onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                            />
                        )}

                        <ParticipantsSelect
                            serviceId={usersCurrentAppointment.serviceId}
                            participants={participants}
                            selectedParticipants={selectedParticipants}
                            updateSelectedParticipants={updateSelectedParticipants}
                            numOfSlots={newEntry.numberOfSlots}
                            statuses={statuses}
                            reservedAppointments={reservedAppointments}
                            roles={roles}
                            areAnySlotsResolved={areAnySlotsResolved}
                            isInPast={isInPast}
                            appointmentDate={newEntry.date}
                            disabled={
                                !canEditSlot(areAnySlotsResolved, 'participant', roles, isInPast)
                            }
                        />

                        <div className={sidePanel.updateSlotsButton}>
                            <Button
                                id="appointmentSlotEditButton"
                                content="Update"
                                disabled={
                                    !canEditSlot(
                                        areAnySlotsResolved,
                                        'updateButton',
                                        roles,
                                        isInPast
                                    ) || isUpdateButtonDisabled
                                }
                                error={errors.button}
                                clearError={() => clearError('button')}
                                customErrorClass="rightSideButtonError"
                            />
                        </div>
                    </form>

                    <StartAppointmentSlotsRows
                        participants={selectedParticipants}
                        appointment={newEntry}
                        disabled={isDisabledStartSlotButton}
                        isUpdateDisabled={isDisabledUpdateSlotButton}
                        hasParticipantAccess={hasParticipantAccess}
                        onPanelClose={onPanelClose}
                        statuses={statuses}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                </div>
            </Panel>
            {showAuditModal && (
                <AuditAppointmentModal
                    isOpen={showAuditModal}
                    label={auditLabel}
                    modalHeadingLabel={auditModalHeadingLabel}
                    participantId={auditParticipantId}
                    onClose={onAuditModalClose}
                />
            )}
        </>
    );
};

UserAppointmentSlotsEdit.propTypes = {
    usersCurrentAppointment: PropTypes.object,
    calendar: PropTypes.string
};

export default UserAppointmentSlotsEdit;
